import React, { useRef, useState } from 'react';

import classNames from 'classnames';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';

import 'react-datepicker/dist/react-datepicker.min.css';

import i18n from 'shared/i18n';

import { formatDate, locales } from 'components/helpers/dates';

export function DatePicker(props) {
  const datePickerRef = props.inputRef || useRef();

  return <AppDatePicker {...props} datePickerRef={datePickerRef} />;
}

function AppDatePicker({
  className,
  datePickerRef,
  disabledPlaceholderText,
  earliestDate,
  fieldError,
  isDisabled,
  latestDate,
  name,
  onCalendarClose,
  onChange,
  placeholderText,
  placement,
  removeErrorStyling,
  value,
}) {
  const [picker, setPicker] = useState('day');

  const locale = i18n.language;

  registerLocale(locale, locales[locale]);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'application.DatePickerField.AppDatePicker',
  });

  const isDayPickerActive = picker === 'day';
  const isMonthPickerActive = picker === 'month';

  const handleDatePickerFocus = (event) => {
    setPicker('day');
    if (removeErrorStyling) removeErrorStyling(event);
  };

  const handleDatePickerChange = (date, event) => {
    if (isDayPickerActive) {
      onChange(name, date);
      // internal open state not available via prop, used class component API
      // would break if internals changed from class component to functional component
      if (event.type === 'click') datePickerRef.current.setOpen(false);
    } else {
      setPicker('day');
    }
  };

  const handleDatePickerHeaderButtonClick = () => {
    if (isDayPickerActive) setPicker('month');
  };

  const isInvalid = !!fieldError?.fieldHighlighted;

  const inputClassName = (() => {
    switch (true) {
      case isDisabled:
        return 'tw-bg-grey-025 tw-text-grey-400 tw-border-grey-200 hover:tw-border-grey-200 focus-within:tw-border-grey-200 hover:focus-within:tw-border-grey-200';
      case isInvalid:
        return 'tw-border-red-600 hover:tw-border-red-600 focus-within:tw-border-red-600 hover:focus-within:tw-border-red-600';
      default:
        return 'tw-border-grey-300 hover:tw-border-grey-400 focus-within:tw-border-blue-300 hover:focus-within:tw-border-blue-300 placeholder:tw-text-grey-300';
    }
  })();

  const handleDatePickerCalenderClose = () => {
    if (onCalendarClose) {
      onCalendarClose(name);
    }
  };

  return (
    <ReactDatePicker
      autoComplete='off'
      calendarClassName='datepicker-calendar'
      className={classNames(
        'datepicker-input field__input tw-font-inter tw-text-m tw-font-normal tw-tracking-auto',
        inputClassName,
        className,
      )}
      dateFormat={['dd/MM/yyyy', 'dd-MM-yyyy']}
      disabled={isDisabled}
      id={name}
      locale={locale}
      maxDate={latestDate}
      minDate={earliestDate}
      name={name}
      onCalendarClose={handleDatePickerCalenderClose}
      onChange={handleDatePickerChange}
      onFocus={handleDatePickerFocus}
      placeholderText={isDisabled ? disabledPlaceholderText : placeholderText}
      popperModifiers={{
        preventOverflow: { enabled: false },
        hide: { enabled: false },
      }}
      popperPlacement={placement}
      ref={datePickerRef}
      renderCustomHeader={(props) =>
        CustomHeader({
          ...props,
          onHeaderButtonClick:
            isDayPickerActive ? handleDatePickerHeaderButtonClick : undefined,
          picker,
          translations: {
            day: {
              prevLabel: i18nT('header.day.prevLabel'),
              nextLabel: i18nT('header.day.nextLabel'),
            },
            month: {
              prevLabel: i18nT('header.month.prevLabel'),
              nextLabel: i18nT('header.month.nextLabel'),
            },
          },
        })
      }
      selected={value}
      shouldCloseOnSelect={false}
      showFullMonthYearPicker={isMonthPickerActive}
      showMonthYearPicker={isMonthPickerActive}
    />
  );
}

export default function DatePickerFieldWrapper(props) {
  const datePickerRef = props.inputRef || useRef();

  return <DatePickerField {...props} datePickerRef={datePickerRef} />;
}

function DatePickerField({
  datePickerRef,
  disabledPlaceholderText = '',
  earliestDate,
  fieldError,
  isDisabled,
  isRequired,
  label,
  latestDate,
  name,
  onCalendarClose,
  onChange,
  placeholderText = '',
  placement = 'bottom-start',
  removeErrorStyling,
  value,
}) {
  return (
    <div className='datepicker-field'>
      <div className='form-group'>
        <label className='field__label tw-font-medium' htmlFor={name}>
          {label} {isRequired && '*'}
        </label>
        <div className='pos-rel tw-flex tw-items-center'>
          <AppDatePicker
            className='tw-min-w-[148px] tw-max-w-[192px]'
            datePickerRef={datePickerRef}
            disabledPlaceholderText={disabledPlaceholderText}
            earliestDate={earliestDate}
            fieldError={fieldError}
            isDisabled={isDisabled}
            latestDate={latestDate}
            name={name}
            onCalendarClose={onCalendarClose}
            onChange={onChange}
            placeholderText={placeholderText}
            placement={placement}
            removeErrorStyling={removeErrorStyling}
            value={value}
          />
        </div>
      </div>
    </div>
  );
}

const CustomHeader = function ({
  date,
  decreaseMonth,
  decreaseYear,
  increaseMonth,
  increaseYear,
  nextMonthButtonDisabled,
  nextYearButtonDisabled,
  onHeaderButtonClick,
  picker,
  prevMonthButtonDisabled,
  prevYearButtonDisabled,
  translations,
}) {
  const pickerMap = {
    day: {
      dateFormat: 'MMMM yyyy',
      prevLabel: translations.day.prevLabel,
      prevOnClick: decreaseMonth,
      prevDisabled: prevMonthButtonDisabled,
      nextLabel: translations.day.nextLabel,
      nextOnClick: increaseMonth,
      nextDisabled: nextMonthButtonDisabled,
    },
    month: {
      dateFormat: 'yyyy',
      prevLabel: translations.month.prevLabel,
      prevOnClick: decreaseYear,
      prevDisabled: prevYearButtonDisabled,
      nextLabel: translations.month.nextLabel,
      nextOnClick: increaseYear,
      nextDisabled: nextYearButtonDisabled,
    },
  };

  const {
    dateFormat,
    nextDisabled,
    nextLabel,
    nextOnClick,
    prevDisabled,
    prevLabel,
    prevOnClick,
  } = pickerMap[picker];

  return (
    <>
      {!prevDisabled && (
        <button
          aria-label={prevLabel}
          className='react-datepicker__navigation react-datepicker__navigation--previous'
          onClick={prevOnClick}
          type='button'
        />
      )}
      <div className='react-datepicker__header'>
        <button
          className={classNames(
            'react-datepicker__header-button',
            onHeaderButtonClick && 'react-datepicker__header-button--clickable',
          )}
          onClick={onHeaderButtonClick}
        >
          {formatDate({ date, format: dateFormat })}
        </button>
      </div>
      {!nextDisabled && (
        <button
          aria-label={nextLabel}
          className='react-datepicker__navigation react-datepicker__navigation--next'
          onClick={nextOnClick}
          type='button'
        />
      )}
    </>
  );
};
